//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { ERC20Wei, ERC20WeiSigned } from './conversions'
import { EXCHANGE_RATE_ONE, yupErc20WeiRequired, yupErc20WeiSigned } from './Enums'

import { ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData } from './SchemaTypes'

import {
  PoolBaseAccountCTO,
  PoolBaseAccountCTOSchemaValues,
  PoolBaseAccountSummaryCTOSchemaValues,
} from './PoolBaseAccountCTO'

export const PoolDynamicAccountOnlyCTOSchemaValues = {
  maxWithdrawRequest: yupErc20WeiRequired
    .label('Max Withdrawal Request')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'maxWithdrawRequest',
        description:
          'The maximum amount that can be requested for withdrawal by the account, denominated in Assets. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  maxRedeemRequest: yupErc20WeiRequired
    .label('Max Tokens Redeemable')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'maxRedeemRequest',
        description:
          'The maximum amount that can be requested for withdrawal by the account, denominated in Vault Tokens. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),

  requestedSharesOf: yupErc20WeiRequired
    .label('Outstanding Withdrawal Requests (Tokens)')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'requestedSharesOf',
        description:
          'The amount of outstanding withdrawal requests for the account, denominated in Vault Tokens. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
  requestedAssetsOf: yupErc20WeiRequired
    .label('Outstanding Withdrawal Requests (Assets)')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'requestedAssetsOf',
        description:
          'The amount of outstanding withdrawal requests for the account, denominated in Assets. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  acceptedShares: yupErc20WeiRequired
    .label('Outstanding Withdrawal Accepted (Tokens)')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'acceptedShares',
        description: 'The amount of outstanding withdrawal requests for the account, denominated in Vault Tokens. ',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
  acceptedAssets: yupErc20WeiRequired
    .label('Outstanding Withdrawal Accepted (Assets)')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'acceptedAssets',
        description: 'The amount of withdrawal requests that have been accepted but not yet repaid.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  principalEarningInterest: yupErc20WeiRequired
    .label('Principal Earning Interest')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'principalEarningInterest',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 10,
        description:
          'The current amount of loan principal earning the interestRate for this account. Debits and credits add/subtract from this value. Fees paid does not subtract from this value.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  assetsDeposited: yupErc20WeiRequired
    .label('Assets Loaned')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'assetsDeposited',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 13,
        description: 'The amount of assets loaned by the account to date. Does not include credits.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  assetsWithdrawn: yupErc20WeiRequired
    .label('Assets Withdrawn')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'assetsWithdrawn',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 14,
        description: 'The amount of assets withdrawn by the account to date. Does not include debits or fees.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  gainLoss: yupErc20WeiSigned
    .label('Gain / Loss')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'gainLoss',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 11,
        description: 'The total gain, loss, or interest accrued for the account to date. Includes debits and credits.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  gainLossInDay: yupErc20WeiSigned
    .label('Daily Gain / Loss')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'gainLossInDay',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 12,
        description:
          'The total gain, loss, or interest accrued for the account for the day. Includes debits and credits.  ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  debits: yupErc20WeiRequired
    .label('Debits')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'debits',
        description: 'The total amount of debits for the account to date. Tokens are burned for debits.  ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  debitsInDay: yupErc20WeiRequired
    .label('Daily Debits')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'debitsInDay',
        description: 'The amount of debits for the account for the day. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  credits: yupErc20WeiRequired
    .label('Credits')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'credits',
        description: 'The total amount of credits for the account to date. Tokens are minted for credits.  ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  creditsInDay: yupErc20WeiRequired
    .label('Daily Credits')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'creditsInDay',
        description: 'The amount of credits for the account for the day.  ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  fees: yupErc20WeiRequired
    .label('Fees')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'fees',
        description: ' The total amount of transaction fees paid by the account to date. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  feesInDay: yupErc20WeiRequired
    .label('Daily Fees')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'feesInDay',
        description: ' The amount of fees paid for the day',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  assetBalance: yupErc20WeiRequired
    .label('Asset Balance')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'assetBalance',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 10,
        description: ' The Asset Balance of the account ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  interestRate: yupErc20WeiRequired
    .label('Loan Fee Rate')
    .required()
    .default('0')
    .meta(
      toFieldMetaData({
        propName: 'interestRate',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 10,
        description:
          'The current annualized interest rate the Borrower will pay on principal earning interest in basis points e.g. 500 = 5.00%',
        stringType: StringTypeEnum.APR,
      })
    ),
  indicativeInterestRate: yupErc20WeiRequired
    .label('Indicative Interest Rate')
    .required()
    .default('0')
    .meta(
      toFieldMetaData({
        propName: 'indicativeInterestRate',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 18,
        description: 'The target rate of return for a dynamic vault',
        stringType: StringTypeEnum.APR,
      })
    ),
  collateralRate: yupErc20WeiRequired
    .label('Collateral Rate')
    .required()
    .default('0')
    .meta(
      toFieldMetaData({
        propName: 'collateralRate',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 19,
        description:
          'The rate of return for the underlying collateral, as defined in the Master Loan Agreement. Only used in vaults with a Dynamic exchange rate.',
        stringType: StringTypeEnum.APR,
      })
    ),
  exchangeRate: yupErc20WeiRequired
    .label('Exchange Rate')
    .required()
    .default(EXCHANGE_RATE_ONE.toString() as ERC20Wei)
    .meta(
      toFieldMetaData({
        propName: 'exchangeRate',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 15,
        description:
          'The current exchange rate at which Vault Tokens and Assets will be exchanged. The Exchange Rate increases by the Daily Interest Rate each day. Uses 18 as a decimal value, so display value should be divided by 100000000000000000 (1 + 18 zeros). For example, 1000000000000000000 = 1.00',
        stringType: StringTypeEnum.EXCHANGE_RATE,
      })
    ),
}

export const PoolDynamicAccountSummaryCTOSchema = yup
  .object({
    ...PoolBaseAccountSummaryCTOSchemaValues,
  })
  .label('PoolDynamicAccountSummaryCTO')
  .meta(
    toFieldMetaData({
      propName: 'PoolDynamicAccountSummaryCTO',
      map: 'PoolDynamicAccountCTO',
      description: 'Pool Dynamic Account CTO',
    })
  )
export const PoolDynamicAccountCTOSchema = yup
  .object({
    ...PoolBaseAccountCTOSchemaValues,
    ...PoolDynamicAccountOnlyCTOSchemaValues,
  })
  .label('PoolDynamicAccountCTO')
  .meta(toFieldMetaData({ propName: 'PoolDynamicAccountCTO', description: 'Pool Account CTO' }))

export const PoolDynamicAccountOnlyCTOSchema = yup.object({
  ...PoolDynamicAccountOnlyCTOSchemaValues,
})

export interface PoolDynamicAccountSummaryCTO extends InferType<typeof PoolDynamicAccountSummaryCTOSchema> {}

export interface PoolDynamicAccountCTO extends PoolBaseAccountCTO {
  maxWithdrawRequest: ERC20Wei
  maxRedeemRequest: ERC20Wei
  requestedAssetsOf: ERC20Wei
  requestedSharesOf: ERC20Wei
  assetBalance: ERC20Wei
  acceptedShares: ERC20Wei
  acceptedAssets: ERC20Wei
  assetsDeposited: ERC20Wei
  assetsWithdrawn: ERC20Wei

  gainLoss: ERC20WeiSigned
  gainLossInDay: ERC20WeiSigned
  debits: ERC20Wei
  debitsInDay: ERC20Wei
  credits: ERC20Wei
  creditsInDay: ERC20Wei
  fees: ERC20Wei
  feesInDay: ERC20Wei
  interestRate: ERC20Wei
  indicativeInterestRate: ERC20Wei
  collateralRate: ERC20Wei
  exchangeRate: ERC20Wei
}

type PoolDynamicAccountCTOInfer = InferType<typeof PoolDynamicAccountCTOSchema>
export const _testAPoolDynamicAccount: PoolDynamicAccountCTO = {} as unknown as PoolDynamicAccountCTOInfer
export const _testBPoolDynamicAccount: PoolDynamicAccountCTOInfer = {} as unknown as PoolDynamicAccountCTO
